/**
 * useSSOProviders Hook
 *
 * React Query hooks for SSO provider management operations.
 * Provides data fetching, mutations, and cache invalidation for SSO providers.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ssoService } from '../../../api/sso.service';
import type {
  SSOProviderConfig,
  SSOProviderCreateDTO,
  SSOProviderUpdateDTO,
} from '../../../types/sso.types';

// ============================================================================
// QUERY KEYS
// ============================================================================

const SSO_QUERY_KEYS = {
  providers: (tenantId: string) => ['sso-providers', tenantId] as const,
  provider: (tenantId: string, providerId: string) => ['sso-providers', tenantId, providerId] as const,
};

// ============================================================================
// QUERY HOOKS
// ============================================================================

/**
 * Hook to fetch all SSO provider configurations for a tenant
 */
export const useSSOProviders = (tenantId: string | undefined) => {
  return useQuery<SSOProviderConfig[], Error>({
    queryKey: SSO_QUERY_KEYS.providers(tenantId ?? ''),
    queryFn: () => ssoService.listProviderConfigs(tenantId!),
    enabled: !!tenantId,
  });
};

/**
 * Hook to fetch a single SSO provider configuration
 */
export const useSSOProvider = (tenantId: string | undefined, providerId: string | undefined) => {
  return useQuery<SSOProviderConfig, Error>({
    queryKey: SSO_QUERY_KEYS.provider(tenantId ?? '', providerId ?? ''),
    queryFn: () => ssoService.getProviderConfig(tenantId!, providerId!),
    enabled: !!tenantId && !!providerId,
  });
};

// ============================================================================
// MUTATION HOOKS
// ============================================================================

/**
 * Hook to create an SSO provider configuration
 */
export const useCreateSSOProvider = (tenantId: string) => {
  const queryClient = useQueryClient();

  return useMutation<SSOProviderConfig, Error, SSOProviderCreateDTO>({
    mutationFn: (data) => ssoService.createProviderConfig(tenantId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SSO_QUERY_KEYS.providers(tenantId) });
    },
  });
};

/**
 * Hook to update an SSO provider configuration
 */
export const useUpdateSSOProvider = (tenantId: string) => {
  const queryClient = useQueryClient();

  return useMutation<
    SSOProviderConfig,
    Error,
    { providerId: string; data: SSOProviderUpdateDTO }
  >({
    mutationFn: ({ providerId, data }) =>
      ssoService.updateProviderConfig(tenantId, providerId, data),
    onSuccess: (_, { providerId }) => {
      queryClient.invalidateQueries({ queryKey: SSO_QUERY_KEYS.providers(tenantId) });
      queryClient.invalidateQueries({ queryKey: SSO_QUERY_KEYS.provider(tenantId, providerId) });
    },
  });
};

/**
 * Hook to delete an SSO provider configuration
 */
export const useDeleteSSOProvider = (tenantId: string) => {
  const queryClient = useQueryClient();

  return useMutation<{ message: string }, Error, string>({
    mutationFn: (providerId) => ssoService.deleteProviderConfig(tenantId, providerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SSO_QUERY_KEYS.providers(tenantId) });
    },
  });
};

/**
 * Hook to test an SSO provider connection
 */
export const useTestSSOProvider = (tenantId: string) => {
  return useMutation({
    mutationFn: (providerId: string) =>
      ssoService.testProviderConnection(tenantId, providerId),
  });
};
